/**
 * Split Test Results panel context
 *
 * A tiny React context that lets the SplitStepNode and the per-variant fork
 * chips (rendered inside ReactFlow / branch edges) signal the JourneysBuilder
 * to open the results panel, and read the shared variant-metrics result.
 *
 * The metrics are fetched ONCE at the builder level and shared here so that:
 *   - the per-variant fork chips (one per branch edge),
 *   - the split-node "Results" hover button, and
 *   - the Results panel drawer
 * all read the same rows and the same single leader cue — no duplicate fetches,
 * and a guaranteed-consistent leader between the canvas chips and the panel.
 */

import { createContext, useContext } from 'react'
import type { UseVariantMetricsResult } from './use-variant-metrics'
import type { LeaderCue, VariantMetricsRow } from './split-test-metrics'

export type SplitTestResultsContextValue = {
    /**
     * Called by the split node / fork chip when the user opens results for a
     * specific split test. `splitId` is the graph node id of the VARIANT_SPLIT —
     * the builder uses it to SCOPE the panel to that one split's variants (a
     * journey can hold several independent split tests).
     */
    openResultsPanel: (splitId: string) => void
    /** Called by the split-step editor's Promote popconfirm. */
    onPromote: (variantId: string | number) => void
    /**
     * Shared variant-metrics fetch result (rows + status). Fetched once at the
     * builder level; consumed by the fork chips and the Results panel.
     */
    metrics: UseVariantMetricsResult
    /**
     * Convenience accessor: the normalized rows when the fetch succeeded,
     * otherwise an empty array.
     */
    rows: VariantMetricsRow[]
    /**
     * Leader cue PER split test, keyed by the split node's graph id. Each split
     * is an independent experiment with its own leader; a card looks up its own
     * split's cue via `leaderCueBySplitId[meta.splitId]`.
     */
    leaderCueBySplitId: Record<string, LeaderCue | null>
    /** True while the metrics fetch is in-flight / not yet started. */
    isLoading: boolean
    /** True when the journey is a draft (never run) — chips show empty state. */
    isDraft: boolean
}

export const SplitTestResultsContext = createContext<SplitTestResultsContextValue>({
    openResultsPanel: () => {},
    onPromote: () => {},
    metrics: { status: 'idle' },
    rows: [],
    leaderCueBySplitId: {},
    isLoading: false,
    isDraft: false,
})

export const useSplitTestResultsContext = (): SplitTestResultsContextValue =>
    useContext(SplitTestResultsContext)
